import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import classNames from 'classnames';
import { motion, MotionStyle } from 'framer-motion';
import Brand from '../Brand/Brand';
import Navigation from '../Navigation/Navigation';
import { monitorMenu, operationsMenu, intelligenceMenu, adminMenu } from '../../menu';
import ThemeContext from '../../contexts/themeContext';
import useAsideTouch from '../../hooks/useAsideTouch';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { useTranslation } from 'react-i18next';
import showNotification from '../../components/extras/showNotification';
import RenewTokensModal from './RenewTokensModal';
import { isFullyRestricted, isMenuEntirelyHidden } from '../../helpers/menuVisibility';

const Aside = () => {
	const { t } = useTranslation(['menu']);
	const { asideStatus, setAsideStatus, mobileDesign } = useContext(ThemeContext);
	const { asideStyle, touchStatus, hasTouchButton } = useAsideTouch();
	const isModernDesign = process.env.REACT_APP_MODERN_DESGIN === 'true';
	const { dir } = useSelector((state: RootState) => state.appStore);
	const dispatch = useDispatch();
	const permissions = useSelector((state: RootState) => state.auth?.permissions);
	const showIntelligenceSection = !isMenuEntirelyHidden(permissions, intelligenceMenu);
	// "Help" under ADMIN is always statically hidden, so Setup/Admin is the
	// only item that ever shows here — the section header should follow it.
	const showAdminSection = !isFullyRestricted(permissions, adminMenu.setup.permissionKeys);

	const canRenewTokens = !isFullyRestricted(
		permissions,
		intelligenceMenu.fleetcopilot.permissionKeys,
	);

	const userId = useSelector(
		(state: RootState) => state.auth?.user?.user?.id || state.auth?.user?.user?.userId,
	);
	const remainingTokens = useSelector(
		(state: RootState) => state.tokenSubscription.remainingTokens,
	);
	const [isRenewingFromSidebar, setIsRenewingFromSidebar] = useState(false);

	const [isRenewModalOpen, setIsRenewModalOpen] = useState(false);

	const [justRenewedAI, setJustRenewedAI] = useState(false);
	const blinkTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		return () => {
			if (blinkTimeoutRef.current) clearTimeout(blinkTimeoutRef.current);
		};
	}, []);

	useEffect(() => {
		const asideElement = document.querySelector('.aside') as HTMLElement;

		if (asideElement && mobileDesign) {
			asideElement.style.removeProperty('left');
		}
	}, [mobileDesign]);

	useEffect(() => {
		if (userId) {
			dispatch.tokenSubscription.refresh();
		}
	}, [userId, dispatch]);

	// null = not fetched yet — don't lock the nav items before we actually
	// know the real count.
	const isOutOfTokens = remainingTokens !== null && remainingTokens <= 0;
	const hasLockedActionIcon = isOutOfTokens && canRenewTokens;
	const handleOpenRenewModal = useCallback(() => {
		setIsRenewModalOpen(true);
	}, []);

	// The modal's own "Renew" button is what actually hits the shared
	// tokenSubscription.renew() action (POST /ingestion/dataIngestion,
	// action: "update_subscription") from the sidebar.
	const handleConfirmRenew = useCallback(async () => {
		if (isRenewingFromSidebar) return;
		setIsRenewingFromSidebar(true);
		try {
			const didRenew = await dispatch.tokenSubscription.renew();
			if (didRenew) {
				showNotification('', 'Your tokens will be renewed within 3 hours', 'success');
				setIsRenewModalOpen(false);
				// Pulse the Fleet Sage / AI Fleet Manager icons for a couple
				// seconds so the unlock reads as an actual event, not just a
				// disabled style silently disappearing.
				setJustRenewedAI(true);
				if (blinkTimeoutRef.current) clearTimeout(blinkTimeoutRef.current);
				blinkTimeoutRef.current = setTimeout(() => setJustRenewedAI(false), 2700);
			} else {
				showNotification('', 'Could not renew tokens. Please try again.', 'danger');
			}
		} catch (err) {
			showNotification('', 'Could not renew tokens. Please try again.', 'danger');
		} finally {
			setIsRenewingFromSidebar(false);
		}
	}, [isRenewingFromSidebar, dispatch]);


	const intelligenceMenuGated = {
		...intelligenceMenu,
		fleetcopilot: {
			...intelligenceMenu.fleetcopilot,
			isDisable: isOutOfTokens,
			...(canRenewTokens && {
				lockedActionIcon: 'Subscriptions',
				lockedActionTitle: 'Renew tokens',
				onLockedAction: handleOpenRenewModal,
				lockedActionPulse: true,
			}),

			iconBlink: justRenewedAI,
		},
		fleetManager: {
			...intelligenceMenu.fleetManager,
			isDisable: isOutOfTokens,
			iconBlink: justRenewedAI,
		},
	};

	return (
		<>
			<motion.aside
				style={dir === 'rtl' ? undefined : (asideStyle as MotionStyle)}
				className={classNames(
					'aside',
					{ open: asideStatus },
					{
						aside_rtl: dir === 'rtl',
						'aside-touch-bar': hasTouchButton && isModernDesign,
						'aside-touch-bar-close': !touchStatus && hasTouchButton && isModernDesign,
						'aside-touch-bar-open': touchStatus && hasTouchButton && isModernDesign,
						'aside-has-locked-action': hasLockedActionIcon,
					},
				)}>
				<div className='aside-head'>
					<Brand asideStatus={asideStatus} setAsideStatus={setAsideStatus} />
				</div>
				<div className='aside-body'>
					{/* INTELLIGENCE */}
					{/* <span className='aside-section-title'>{t('ARTIFICIAL INTELLIGENCE')}</span> */}
					{showIntelligenceSection && (
						<span className='aside-section-title'>{t('ARTIFICIAL INTELLIGENCE')}</span>
					)}
					<Navigation menu={intelligenceMenuGated} id='aside-intelligence' />

					{/* MONITOR */}
					<span className='aside-section-title'>{t('MONITOR')}</span>
					<Navigation menu={monitorMenu} id='aside-monitor' />

					{/* OPERATIONS */}
					<span className='aside-section-title'>{t('OPERATIONS')}</span>
					<Navigation menu={operationsMenu} id='aside-operations' />

					{/* ADMIN */}
					{showAdminSection && <span className='aside-section-title'>{t('ADMIN')}</span>}
					<Navigation menu={adminMenu} id='aside-admin' />
				</div>
			</motion.aside>
			<RenewTokensModal
				isOpen={isRenewModalOpen}
				setIsOpen={setIsRenewModalOpen}
				onRenew={handleConfirmRenew}
				isRenewing={isRenewingFromSidebar}
			/>
		</>
	);
};

export default Aside;
